import { Group, Raycaster, Vector2 } from 'three/webgpu'
import DeviceButton from './DeviceButton'
import DeviceSlider from './DeviceSlider'
import DeviceDial from './DeviceDial'

export default class DeviceControls extends Group {
	static defaults = {
		model: null,
		camera: null,
		project: null,
		listener: null,
		sheet: 'Button',
		cursor: 'pointer',
		grab: 'grab',
		types: [
			{ match: 'volume_slider', type: DeviceSlider },
			{ match: 'dial', type: DeviceDial },
			{ match: 'button', type: DeviceButton },
		],
		options: {},
	}

	constructor(options = {}) {
		super()

		this.options = { ...DeviceControls.defaults, ...options }

		this.controls = []
		this.meshes = []
		this.hovered = null
		this.active = null
		this.pointer = new Vector2()
		this.pixels = new Vector2()
		this.raycaster = new Raycaster()

		this.options.model.add(this)
		this.#build()
		this.#listen()
	}

	#build() {
		const { model, camera, project, listener, sheet, types, options } = this.options
		const found = []

		model.traverse(child => {
			if (!child.isMesh) return

			const name = child.name.toLowerCase()
			const entry = types.find(({ match }) => name.includes(match))

			if (entry) found.push({ entry, source: child })
		})

		for (const { entry, source } of found) {
			const control = new entry.type({
				source,
				camera,
				listener,
				sheet: entry.type.draggable ? null : project?.sheet(`${sheet} ${DeviceControls.label(source.name)}`),
				...options[entry.match],
			})

			if (entry.type === DeviceDial) this.dial = control

			this.controls.push(control)
			this.meshes.push(source)
			this.add(control)
		}
	}

	static label(name) {
		return name.replace(/_?baked$/i, '').replace(/^button_/i, '') || name
	}

	#listen() {
		this._onMove = event => this.#move(event)
		this._onDown = () => this.#down()
		this._onUp = () => this.#up()

		window.addEventListener('pointermove', this._onMove)
		window.addEventListener('pointerdown', this._onDown)
		window.addEventListener('pointerup', this._onUp)
		window.addEventListener('pointercancel', this._onUp)
	}

	#move(event) {
		this.pixels.set(event.clientX, event.clientY)

		if (this.active) return this.active.drag?.(this.pixels)

		this.pointer.set((event.clientX / window.innerWidth) * 2 - 1, -(event.clientY / window.innerHeight) * 2 + 1)
		this.raycaster.setFromCamera(this.pointer, this.options.camera)

		const hit = this.raycaster.intersectObjects(this.meshes, false)[0]
		const control = hit ? hit.object.parent : null

		if (control === this.hovered) return

		this.hovered = control
		document.body.style.cursor = control
			? control.constructor.draggable
				? this.options.grab
				: this.options.cursor
			: ''
	}

	#down() {
		if (!this.hovered) return

		this.active = this.hovered
		this.active.press(this.pixels)
	}

	#up() {
		this.active?.release()
		this.active = null
	}

	update(delta, elapsed) {
		for (const control of this.controls) control.update(delta, elapsed)
	}

	dispose() {
		window.removeEventListener('pointermove', this._onMove)
		window.removeEventListener('pointerdown', this._onDown)
		window.removeEventListener('pointerup', this._onUp)
		window.removeEventListener('pointercancel', this._onUp)

		document.body.style.cursor = ''

		for (const control of this.controls) control.dispose()

		this.dial = null
		this.controls.length = 0
		this.meshes.length = 0
		this.removeFromParent()
	}
}
